import { useEffect } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Message, Profile } from '@/types/chat';

const PAGE_SIZE = 50;

export const useChatMessages = (conversationId: string | null) => {
    const queryClient = useQueryClient();

    // Real-time subscription for new messages
    useEffect(() => {
        if (!conversationId) return;

        const channel = supabase
            .channel(`chat_messages:${conversationId}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'chat_messages',
                    // Removing filter to ensure we get events (filtering client-side as fallback)
                    // filter: `conversation_id=eq.${conversationId}`, 
                },
                (payload: any) => {
                    console.log('[MessageSubscription] Raw payload:', payload);
                    // Filter client-side
                    if (payload.new && payload.new.conversation_id === conversationId) {
                        console.log('[MessageSubscription] Received new message for this conversation');
                        // Invalidate the query to refetch messages when a new one arrives
                        queryClient.invalidateQueries({ queryKey: ['chat_messages', conversationId] });
                    }
                }
            )
            .subscribe((status) => {
                console.log(`[MessageSubscription] Status for ${conversationId}:`, status);
                if (status === 'SUBSCRIBED') {
                    console.log(`[MessageSubscription] Subscribed to insert events on ${conversationId}`);
                }
            });

        return () => {
            supabase.removeChannel(channel);
        };
    }, [conversationId, queryClient]);

    const fetchMessages = async ({ pageParam = 0 }: { pageParam?: number }) => {
        if (!conversationId) return [];

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        const { data, error } = await supabase
            .from('chat_messages')
            .select('*')
            .eq('conversation_id', conversationId)
            .order('created_at', { ascending: false })
            .range(pageParam * PAGE_SIZE, (pageParam + 1) * PAGE_SIZE - 1);

        if (error) throw error;

        // Fetch sender profiles
        const senderIds = [...new Set(data.map(m => m.sender_id))];
        let profiles: Record<string, Profile> = {};

        if (senderIds.length > 0) {
            const { data: profilesData } = await supabase
                .from('profiles')
                .select('id, name, avatar_url, email')
                .in('id', senderIds);

            if (profilesData) {
                profilesData.forEach(p => {
                    profiles[p.id] = p as Profile;
                });
            }
        }

        return data.map(msg => ({
            ...msg,
            sender: profiles[msg.sender_id] || { id: msg.sender_id, name: 'Unknown' } // Fallback
        })) as Message[];
    };

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading
    } = useInfiniteQuery({
        queryKey: ['chat_messages', conversationId],
        queryFn: fetchMessages,
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            return lastPage.length === PAGE_SIZE ? allPages.length : undefined;
        },
        enabled: !!conversationId,
        staleTime: 0,
        refetchInterval: 3000, // Fallback: poll every 3 seconds if Realtime fails
    });

    return {
        messages: data?.pages.flat() || [],
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading
    };
};
